/*
 * /producer/$producerId (page)
 */
import {
	Badge,
	Button,
	Card,
	CardContent,
	CardHeader,
	CardTitle,
	Container,
	Form,
	FormButtons,
	InputFormField,
	NumberInputFormField,
	Page,
	PageHeader,
	Placeholder,
	SelectFormField,
	Tabs,
	TabsContent,
	TabsList,
	TabsTrigger,
	TextareaFormField,
	CheckboxFormField,
	toast,
} from "@emr/react-ui"
import { createFileRoute } from "@tanstack/react-router"
import { Package, Plus } from "lucide-react"
import { useState } from "react"
import { z } from "zod"
import { ProducerForm } from "../../features/producer/producer-form"
import { trpc } from "../../trpc"
import type { Product } from "@emr/db/client"

export const Route = createFileRoute("/producer/$producerId")({
	component: ProducerPage,
})

/* ─── Product type options ─────────────────────────────────────────────── */

const PRODUCT_TYPE_OPTIONS = [
	{ label: "Dried Flower", value: "1" },
	{ label: "Oil", value: "2" },
	{ label: "Concentrate", value: "3" },
	{ label: "Topical Ointment", value: "4" },
	{ label: "Oral Spray", value: "5" },
	{ label: "Seeds", value: "6" },
	{ label: "Edible", value: "7" },
	{ label: "Capsule", value: "8" },
	{ label: "Tablet", value: "9" },
	{ label: "Patch", value: "10" },
	{ label: "Tincture", value: "11" },
	{ label: "Suppository", value: "12" },
]

const MEASUREMENT_OPTIONS = [
	{ label: "Grams (g)", value: "1" },
	{ label: "Millilitres (ml)", value: "2" },
	{ label: "Each (unit)", value: "3" },
]

/* ─── Product form schema ──────────────────────────────────────────────── */

const productFormSchema = z.object({
	name: z.string().min(1, "Name is required"),
	productType: z.coerce.number().int().min(1),
	quantity: z.coerce.number().min(0).optional(),
	measurement: z.coerce.number().int().optional(),
	description: z.string().optional().nullable(),
	discontinued: z.boolean().default(false),
	currentlyUnavailable: z.boolean().default(false),
})

type ProductFormValues = z.infer<typeof productFormSchema>

/* ─── Page ─────────────────────────────────────────────────────────────── */

function ProducerPage() {
	const { producerId } = Route.useParams()
	const [showAddProduct, setShowAddProduct] = useState(false)

	const { data: producer, isLoading: producerLoading } =
		trpc.producer.getByFilter.useQuery({ where: { id: producerId } })

	const {
		data: products,
		isLoading: productsLoading,
		refetch: refetchProducts,
	} = trpc.product.getAllByFilter.useQuery({
		where: { producerId, active: true },
	})

	const createProduct = trpc.product.create.useMutation({
		onSuccess: () => {
			refetchProducts()
			setShowAddProduct(false)
			toast.success("Product added")
		},
		onError: (e) => toast.error(e.message),
	})

	async function handleAddProduct(values: ProductFormValues) {
		try {
			await createProduct.mutateAsync({ ...values, producerId })
		} catch (e: any) {
			return e?.message ?? "An error occurred"
		}
	}

	const isLoading = producerLoading || productsLoading

	if (isLoading) {
		return (
			<Page>
				<Container width="xl" padding="lg">
					<Placeholder title="Loading..." />
				</Container>
			</Page>
		)
	}

	return (
		<Page>
			<Container width="xl" padding="lg" gap="lg">
				<PageHeader title={producer?.name ?? "Producer"} />

				<Tabs defaultValue={producer ? "details" : "products"}>
					<TabsList>
						{producer && <TabsTrigger value="details">Details</TabsTrigger>}
						<TabsTrigger value="products">
							Products
							{products && products.length > 0 && (
								<Badge className="ml-2">{products.length}</Badge>
							)}
						</TabsTrigger>
					</TabsList>

					{/* ── Details tab ── */}
					{producer && (
						<TabsContent value="details" className="mt-4">
							<Card>
								<CardHeader>
									<CardTitle>Producer Information</CardTitle>
								</CardHeader>
								<CardContent>
									<ProducerForm producer={producer} />
								</CardContent>
							</Card>
						</TabsContent>
					)}

					{/* ── Products tab ── */}
					<TabsContent value="products" className="mt-4 flex flex-col gap-4">

						{/* Add product form */}
						{showAddProduct ? (
							<Card>
								<CardHeader>
									<CardTitle>Add Product</CardTitle>
								</CardHeader>
								<CardContent>
									<Form<ProductFormValues>
										schema={productFormSchema}
										defaultValues={{
											discontinued: false,
											currentlyUnavailable: false,
										}}
										onSubmit={handleAddProduct}
										className="flex flex-col gap-4"
									>
										<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
											<InputFormField
												name="name"
												label="Product Name"
												placeholder="e.g. Indica Dried Flower"
												required
											/>
											<SelectFormField
												name="productType"
												label="Product Type"
												options={PRODUCT_TYPE_OPTIONS}
												placeholder="Select type"
												required
											/>
										</div>

										<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
											<NumberInputFormField
												name="quantity"
												label="Quantity"
												placeholder="0"
												min={0}
												step={0.1}
											/>
											<SelectFormField
												name="measurement"
												label="Measurement"
												options={MEASUREMENT_OPTIONS}
												placeholder="Select unit"
											/>
										</div>

										<TextareaFormField
											name="description"
											label="Description"
											placeholder="Product description..."
										/>

										<div className="flex flex-row gap-6">
											<CheckboxFormField
												name="discontinued"
												label="Discontinued"
											/>
											<CheckboxFormField
												name="currentlyUnavailable"
												label="Currently Unavailable"
											/>
										</div>

										<div className="flex gap-2">
											<FormButtons submitLabel="Add Product" />
											<Button
												type="button"
												variant="outline"
												onClick={() => setShowAddProduct(false)}
											>
												Cancel
											</Button>
										</div>
									</Form>
								</CardContent>
							</Card>
						) : (
							<div className="flex justify-end">
								<Button onClick={() => setShowAddProduct(true)}>
									<Plus className="w-4 h-4 mr-1" />
									Add Product
								</Button>
							</div>
						)}

						{/* Product list */}
						{!products || products.length === 0 ? (
							<Placeholder title="No products yet" />
						) : (
							<div className="flex flex-col gap-3">
								{products.map((product) => (
									<ProductCard key={product.id} product={product} onUpdated={refetchProducts} />
								))}
							</div>
						)}
					</TabsContent>
				</Tabs>
			</Container>
		</Page>
	)
}

/* ─── Product card ─────────────────────────────────────────────────────── */

function ProductCard({
	product,
	onUpdated,
}: {
	product: Product
	onUpdated: () => void
}) {
	const [editing, setEditing] = useState(false)
	const updateProduct = trpc.product.update.useMutation({
		onSuccess: () => {
			onUpdated()
			setEditing(false)
			toast.success("Product updated")
		},
		onError: (e) => toast.error(e.message),
	})

	const typeName =
		PRODUCT_TYPE_OPTIONS.find((o) => o.value === String(product.productType))
			?.label ?? `Type ${product.productType}`
	const measurementLabel =
		MEASUREMENT_OPTIONS.find((o) => o.value === String(product.measurement))?.label ?? null

	async function handleUpdate(values: ProductFormValues) {
		try {
			await updateProduct.mutateAsync({ id: product.id, ...values })
		} catch (e: any) {
			return e?.message ?? "An error occurred"
		}
	}

	if (editing) {
		return (
			<Card>
				<CardHeader>
					<CardTitle className="text-base">{product.name}</CardTitle>
				</CardHeader>
				<CardContent>
					<Form<ProductFormValues>
						schema={productFormSchema}
						defaultValues={{
							name: product.name,
							productType: String(product.productType) as any,
							quantity: product.quantity ?? undefined,
							measurement: product.measurement != null ? (String(product.measurement) as any) : undefined,
							description: product.description,
							discontinued: product.discontinued,
							currentlyUnavailable: product.currentlyUnavailable,
						}}
						onSubmit={handleUpdate}
						className="flex flex-col gap-4"
					>
						<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
							<InputFormField name="name" label="Product Name" required />
							<SelectFormField
								name="productType"
								label="Product Type"
								options={PRODUCT_TYPE_OPTIONS}
								required
							/>
						</div>
						<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
							<NumberInputFormField
								name="quantity"
								label="Quantity"
								min={0}
								step={0.1}
							/>
							<SelectFormField
								name="measurement"
								label="Measurement"
								options={MEASUREMENT_OPTIONS}
							/>
						</div>
						<TextareaFormField name="description" label="Description" />
						<div className="flex flex-row gap-6">
							<CheckboxFormField name="discontinued" label="Discontinued" />
							<CheckboxFormField name="currentlyUnavailable" label="Currently Unavailable" />
						</div>
						<div className="flex gap-2">
							<FormButtons submitLabel="Save" />
							<Button type="button" variant="outline" onClick={() => setEditing(false)}>
								Cancel
							</Button>
						</div>
					</Form>
				</CardContent>
			</Card>
		)
	}

	return (
		<Card>
			<CardContent className="flex items-center justify-between py-4">
				<div className="flex items-center gap-3">
					<Package className="w-5 h-5 text-muted-foreground shrink-0" />
					<div>
						<p className="font-medium">{product.name}</p>
						<p className="text-sm text-muted-foreground">
							{typeName}
							{product.quantity != null && ` · ${product.quantity}${measurementLabel ? ` ${measurementLabel}` : ""}`}
						</p>
					</div>
				</div>
				<div className="flex items-center gap-2">
					{product.discontinued && (
						<Badge variant="destructive">Discontinued</Badge>
					)}
					{product.currentlyUnavailable && (
						<Badge variant="secondary">Unavailable</Badge>
					)}
					<Button
						variant="outline"
						size="sm"
						onClick={() => setEditing(true)}
					>
						Edit
					</Button>
				</div>
			</CardContent>
		</Card>
	)
}
